import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { StatusPill, Delta, Table, TableHead, TableRow } from "@/components/vb";
import { TRUST_SIGNALS_TOTAL } from "@/types/vb";
import type { AnalysisShadow } from "@/types/vb";

/** Period · Status · Signals · Visibility · Share of voice · View. */
const ANALYSES_COLS = "1.1fr 1fr 1fr 1fr 1fr 64px";

/** US-format run date, e.g. "May 29, 2026" (the run's completion, else creation).
 *  Guarded so one bad timestamp can't crash the table. */
function runDate(a: AnalysisShadow): string {
  const iso = a.completedAt ?? a.createdTs;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return format(d, "MMM d, yyyy");
}

function pct(v: number | null): string {
  return v == null ? "-" : `${Math.round(v * 100)}%`;
}

function AnalysisRow({
  a,
  buyerId,
  active,
  signals,
  signalsDelta,
}: {
  a: AnalysisShadow;
  buyerId: number;
  active: boolean;
  /** Effective signals count for this row (derived for the latest run before
   *  its column is healed; the row's own column otherwise). */
  signals: number | null;
  signalsDelta: number | null;
}) {
  const navigate = useNavigate();
  const done = a.status === "DONE";
  const inFlight = a.status === "PENDING" || a.status === "IN_PROGRESS";
  const verdictHref = `/virtual-buyer/${buyerId}/verdict/${a.id}`;
  const runHref = `/virtual-buyer/${buyerId}/run/${a.id}`;
  const label = runDate(a);

  // DONE rows open the verdict; in-flight rows open the live run progress.
  // The whole row is a click convenience; the focusable <Link> is the real
  // navigation control.
  const rowHref = done ? verdictHref : inFlight ? runHref : null;
  const onRowClick = (e: React.MouseEvent) => {
    if (!rowHref) return;
    if ((e.target as HTMLElement).closest("a")) return;
    if (!window.getSelection()?.isCollapsed) return;
    navigate(rowHref);
  };

  return (
    <TableRow interactive selected={active} onClick={onRowClick}>
      <span className="vb-an-date" data-label="Date">{label}</span>

      <span data-label="Status">
        <StatusPill status={a.status} />
      </span>

      {done && signals != null ? (
        <span className="vb-an-num tnum" data-label="Signals">
          {signals}/{TRUST_SIGNALS_TOTAL}
          <Delta value={signalsDelta} />
        </span>
      ) : (
        <span className="vb-cell-empty" data-label="Signals">-</span>
      )}

      <span className={done && a.visibility != null ? "vb-an-num tnum" : "vb-cell-empty"} data-label="Visibility">
        {done ? pct(a.visibility) : "-"}
      </span>

      <span className={done && a.sov != null ? "vb-an-num tnum" : "vb-cell-empty"} data-label="Share of voice">
        {done ? pct(a.sov) : "-"}
      </span>

      {done ? (
        <Link className="vb-open-link" to={verdictHref} aria-label={`Open ${label} verdict`}>
          View
          <ArrowRight size={13} aria-hidden="true" />
        </Link>
      ) : inFlight ? (
        <Link className="vb-open-link" to={runHref} aria-label={`View progress for ${label}`}>
          View
          <ArrowRight size={13} aria-hidden="true" />
        </Link>
      ) : (
        <span className="vb-an-nodata">{a.status === "FAILED" ? "no data" : "-"}</span>
      )}
    </TableRow>
  );
}

/** VB-D · per-buyer analysis history. Newest-first; DONE rows open the verdict
 *  (VB-F1), in-flight rows open the live run progress, failed rows show no data.
 *  The latest DONE run is highlighted. */
export function AnalysesTable({
  analyses,
  buyerId,
  latestDoneId,
  latestSignals,
}: {
  analyses: AnalysisShadow[];
  buyerId: number;
  latestDoneId?: number;
  /** Derived signals for the latest DONE run - used for that row (and its delta)
   *  so it agrees with the exposure card before the denormalized column heals. */
  latestSignals?: number | null;
}) {
  // Effective signals per row: the derived value for the latest DONE run (before
  // its column heals), each row's own column otherwise.
  const signalsFor = (a: AnalysisShadow): number | null =>
    a.id === latestDoneId ? (latestSignals ?? a.signalsExposedCount) : a.signalsExposedCount;

  // Per-DONE-row signals delta vs the next-older DONE run (rows are newest-first).
  // Only computable where both runs have a signals count; null otherwise.
  const deltaById = useMemo(() => {
    const done = analyses.filter((a) => a.status === "DONE");
    const map = new Map<number, number>();
    for (let i = 0; i < done.length - 1; i++) {
      const cur = signalsFor(done[i]);
      const prev = signalsFor(done[i + 1]);
      if (cur != null && prev != null) map.set(done[i].id, cur - prev);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyses, latestDoneId, latestSignals]);

  return (
    <Table columns={ANALYSES_COLS} minWidth={560} tight>
      <TableHead>
        <span>Date</span>
        <span>Status</span>
        <span>Signals</span>
        <span>Visibility</span>
        <span>Share of voice</span>
        <span aria-hidden="true" />
      </TableHead>
      {analyses.map((a) => (
        <AnalysisRow
          key={a.id}
          a={a}
          buyerId={buyerId}
          active={a.id === latestDoneId}
          signals={signalsFor(a)}
          signalsDelta={deltaById.get(a.id) ?? null}
        />
      ))}
    </Table>
  );
}

export default AnalysesTable;
